'use client';

import { useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  ArrowRight,
} from 'lucide-react';
import type { PrioritizedFinding } from '@/lib/audit/report-organizer';
import { getEffortLabel } from '@/lib/audit/report-organizer';
import { CriterionTag } from './CriterionTag';
import styles from './ActionPlan.module.css';

interface ActionPlanProps {
  findings: PrioritizedFinding[];
  locale?: string;
  onFindingClick?: (finding: PrioritizedFinding) => void;
}

// Get accent color class for alternating timeline numbers
function getAccentClass(index: number): string {
  const accents = ['accent1', 'accent2', 'accent3'];
  return accents[index % 3];
}

/**
 * Action Plan Component
 * Displays top 5 priority fixes in a timeline format
 * Uses timeline/steps pattern per design system with alternating accent colors
 */
export function ActionPlan({ findings, locale = 'en', onFindingClick }: ActionPlanProps) {
  const i18n = useMemo(
    () => ({
      en: {
        title: 'Action Plan',
        subtitle: 'Do These First',
        priorityLabel: 'Priority',
        effortLabel: 'Effort',
        viewDetails: 'View Details',
        noActions: 'No priority actions identified',
        intro: 'Focus on these high-impact fixes to improve your UX score.',
      },
      es: {
        title: 'Plan de Acción',
        subtitle: 'Haz Esto Primero',
        priorityLabel: 'Prioridad',
        effortLabel: 'Esfuerzo',
        viewDetails: 'Ver Detalles',
        noActions: 'No se identificaron acciones prioritarias',
        intro: 'Enfócate en estas correcciones de alto impacto para mejorar tu puntuación UX.',
      },
    }),
    []
  );

  const t = i18n[locale as 'en' | 'es'] || i18n.en;
  const displayFindings = findings.slice(0, 5);

  if (!findings || findings.length === 0) {
    return (
      <section className={styles.container}>
        <div className={styles.header}>
          <span className={styles.sectionLabel}>{t.title}</span>
          <h2 className={styles.title}>{t.subtitle}</h2>
        </div>
        <p className={styles.empty}>{t.noActions}</p>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      {/* Section Header */}
      <div className={styles.header}>
        <span className={styles.sectionLabel}>{t.title}</span>
        <h2 className={styles.title}>{t.subtitle}</h2>
        <p className={styles.intro}>{t.intro}</p>
      </div>

      {/* Timeline List with connecting line */}
      <div className={styles.timeline}>
        {/* Vertical connecting line */}
        <div className={styles.timelineTrack} />

        {displayFindings.map((finding, index) => (
          <div
            key={finding.principleId || index}
            className={`${styles.timelineItem} ${index === displayFindings.length - 1 ? styles.lastItem : ''}`}
            onClick={() => onFindingClick?.(finding)}
            role={onFindingClick ? 'button' : undefined}
            tabIndex={onFindingClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onFindingClick?.(finding);
              }
            }}
          >
            {/* Timeline Number with alternating accent colors */}
            <div className={styles.timelineNumber}>
              <span className={`${styles.number} ${styles[getAccentClass(index)]}`}>
                {index + 1}
              </span>
            </div>

            {/* Content */}
            <div className={styles.content}>
              {/* Severity Badge */}
              <div className={styles.badges}>
                <span className={`${styles.severityBadge} ${styles[finding.severity]}`}>
                  {getSeverityIcon(finding.severity)}
                  <span>{finding.severity}</span>
                </span>
                {finding.effortEstimate && (
                  <span className={styles.effortBadge}>
                    <Clock size={12} />
                    <span>{getEffortLabel(finding.effortEstimate)}</span>
                  </span>
                )}
                <CriterionTag code={finding.auditCode} categoryLabel={finding.auditCategoryLabel} />
              </div>

              {/* Title */}
              <h3 className={styles.findingTitle}>
                {finding.principleTitle || 'UX Issue'}
              </h3>

              {/* Description */}
              <p className={styles.description}>
                {truncateText(finding.description, 180)}
              </p>

              {/* Quick Recommendation */}
              {finding.recommendation && (
                <div className={styles.recommendation}>
                  <Zap size={14} className={styles.recommendationIcon} />
                  <span>{truncateText(finding.recommendation, 120)}</span>
                </div>
              )}

              {/* Priority Reason */}
              <div className={styles.priorityRow}>
                <span className={styles.priorityReason}>
                  {finding.priorityReason}
                </span>
                {onFindingClick && (
                  <button className={styles.detailsLink} type="button">
                    {t.viewDetails}
                    <ArrowRight size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Helper functions
function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'critical':
      return <AlertCircle size={12} />;
    case 'high':
      return <AlertTriangle size={12} />;
    default:
      return <CheckCircle size={12} />;
  }
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export default ActionPlan;
